import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  GECKO_CONSTS,
  GeckoCardTable,
  GeckoDialogService,
  GeckoModalService,
  GeckoToastService,
} from 'ui';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';

import SalesforceFieldModal from '../components/SalesforceFieldModal.jsx';
import RadiusFieldModal from '../components/RadiusFieldModal.jsx';
import ConnectFieldModal from '../components/ConnectFieldModal.jsx';
import GeckoPagination from 'services/gecko-pagination';
import DynamicsFieldModal from '../components/DynamicsFieldModal.jsx';
import EngageFieldModal from '../components/EngageFieldModal.jsx';

import './index.scss';
import RadiusRecurringImportCard from './RadiusRecurringImportCard';
import DynamicsRecurringImportCard from './DynamicsRecurringImportCard';

import { createOptionsListFromRawOptions } from 'utilities/customFieldUtils';
import WebhookEditModal from '../components/WebhookEditModal';

export class Integration extends Component {
  createField(field) {
    const newField = {
      account_id: this.props.accountId,
      custom_field: true,
      required_for_sync: false,
      field_options: field.raw_options
        ? createOptionsListFromRawOptions(field.raw_options)
        : undefined,
      ...field,
    };
    return GeckoRequest.post('/fields/', newField)
      .then((newField) => {
        new GeckoToastService().success(
          `The field ${newField.label} has been created`
        );
        return newField;
      })
      .catch(renderError);
  }

  mapField(mapping) {
    if (mapping.id) {
      GeckoRequest.post(`/models/field_mappings/${mapping.id}`, mapping)
        .then(() => {
          new GeckoToastService().success('Mapping has been updated');
        })
        .catch(renderError);
    } else {
      GeckoRequest.post(`/models/field_mappings`, mapping)
        .then(() => {
          new GeckoToastService().success('New mapping has been created');
        })
        .catch(renderError);
    }
  }

  deleteMapping(mapping_id) {
    GeckoRequest.delete(`/models/field_mappings/${mapping_id}`)
      .then(() => {
        new GeckoToastService().success('Mapping deleted');
      })
      .catch(renderError);
  }

  availableFields(currentValues) {
    const mappingIds = this.props.field_mappings
      .filter((map) => map.integration_id === this.props.integration.id)
      .map((map) => map.field_id);
    return this.props.fields.filter((field) => {
      return (
        mappingIds.indexOf(field.id) === -1 ||
        field.id === currentValues.field_id
      );
    });
  }

  availableCRMfields(currentValues) {
    const mappingValues = this.props.field_mappings.map(
      (m) => m.external_id && !m.configuration
    );
    return this.props.crmFields.filter((field) => {
      return (
        mappingValues.indexOf(field.key) === -1 ||
        field.key === currentValues.external_id
      );
    });
  }

  openEditModal(currentRow = {}) {
    let modal;
    switch (this.props.integration.type) {
      case 'salesforce':
        const crmModules = this.props.crmModules.filter(
          (module) =>
            module.name === this.props.integration.configuration.module
        );
        modal = (
          <SalesforceFieldModal
            crmModules={crmModules}
            crmFields={this.availableCRMfields(currentRow)}
            fields={this.availableFields(currentRow)}
            values={currentRow}
          />
        );
        break;
      case 'radius':
        modal = (
          <RadiusFieldModal
            crmModules={this.props.crmModules}
            crmFields={this.availableCRMfields(currentRow)}
            fields={this.availableFields(currentRow)}
            values={currentRow}
          />
        );
        break;
      case 'connect':
        modal = (
          <ConnectFieldModal
            crmFields={this.availableCRMfields(currentRow)}
            fields={this.availableFields(currentRow)}
            values={currentRow}
          />
        );
        break;
      case 'dynamics':
        modal = (
          <DynamicsFieldModal
            crmModules={this.props.crmModules}
            crmFields={this.availableCRMfields(currentRow)}
            fields={this.availableFields(currentRow)}
            values={currentRow}
            config={this.props.integration.configuration}
          />
        );
        break;
      case 'engage':
        if (!currentRow.configuration) {
          currentRow.configuration = {};
        }
        modal = (
          <EngageFieldModal
            crmFields={this.availableCRMfields(currentRow)}
            fields={this.availableFields(currentRow)}
            values={currentRow}
          />
        );
        break;
      case 'webhook':
        modal = <WebhookEditModal />;
        break;
      default:
        modal = <p>Unknown Integration</p>;
    }
    return new GeckoModalService()
      .open(() => {
        return modal;
      })
      .then((result) => {
        if (!result) {
          return;
        }
        if (!result.map_to_existing) {
          const label = result.external_label;
          const key = result.key;
          const newField = Object.assign({}, result.new_field, { key, label });
          this.createField(newField).then((newField) => {
            this.mapField({
              id: result.id,
              external_id: result.external_id,
              external_label: result.external_label,
              field_id: newField.id,
              module: result.module,
              active: result.active,
              configuration: result.configuration || null,
              integration_id: this.props.integration.id,
            });
          });
        } else {
          this.mapField({
            id: result.id,
            external_id: result.external_id,
            external_label: result.external_label,
            field_id: result.field_id,
            module: result.module,
            active: result.active,
            configuration: result.configuration || null,
            integration_id: this.props.integration.id,
          });
        }
      })
      .catch(console.error);
  }

  headerButtons = [
    {
      preset: GECKO_CONSTS.BTN_PRESET_ADD,
      handler: this.openEditModal.bind(this),
    },
    {
      preset: GECKO_CONSTS.BTN_PRESET_ACTIONS_COMPACT,
      items: [
        {
          name: 'Remove Integration',
          handler: () => {
            new GeckoDialogService()
              .confirm('Are you sure you want to remove this Integration?')
              .then((confirm) => {
                if (confirm) {
                  GeckoRequest.delete(
                    `/integration/${this.props.integration.type}`
                  )
                    .then(() => {
                      new GeckoToastService().success(
                        `Integration has successfully been deleted.`
                      );
                      GeckoLocation.goTo('/settings/integrations');
                    })
                    .catch(renderError);
                }
              })
              .catch(() => {});
          },
        },
      ],
    },
  ];

  tableCols = [
    {
      key: 'external_label',
      name: 'CRM Field',
      render: (label, field) => label || field.external_id,
      width: '50%',
    },
    {
      key: 'field_id',
      name: 'Gecko Field',
      render: (value) =>
        this.props.fields.find((f) => f.id === value).label || null,
      width: '50%',
    },
    {
      type: GECKO_CONSTS.TABLE_CELL_TYPE_DROPDOWN,
      items: [
        {
          name: 'Edit Mapping',
          handler: this.openEditModal.bind(this),
        },
        {
          name: 'Remove Mapping',
          icon: 'far fa-trash-alt',
          handler: (row) => this.deleteMapping(row.id),
        },
      ],
    },
  ];

  integrationName() {
    let int_name;
    switch (this.props.integration.type) {
      case 'salesforce':
        int_name = `Salesforce "${this.props.integration.configuration.module}"`;
        break;
      case 'radius':
        int_name = 'Radius';
        break;
      case 'connect':
        int_name = 'Connect';
        break;
      case 'dynamics':
        int_name = 'Microsoft Dynamics';
        break;
      case 'engage':
        int_name = 'Gecko Engage';
        break;
      case 'webhook':
        int_name = 'Webhook';
        break;
      case 'slack':
        int_name = 'Slack';
        break;
      case 'teams':
        int_name = 'Teams';
        break;
      default:
        int_name = 'Custom';
    }
    return `${int_name} Integration`;
  }

  render() {
    const [field_mappings, pagination] = GeckoPagination.paginate({
      page: this.props.params.page,
      order_by: 'external_id',
    })(
      this.props.field_mappings.filter(
        (mapping) => mapping.integration_id === this.props.integration.id
      )
    );
    // map the mappings object to an object of key and value so it can be used with GeckoTable
    return (
      <div className="content-margin">
        <GeckoCardTable
          name={this.integrationName()}
          cols={this.tableCols}
          rows={field_mappings}
          headerBtns={this.headerButtons}
          rowHandler={this.openEditModal.bind(this)}
          pagination={pagination}
        />
        {this.props.integration.type === 'radius' ? (
          <RadiusRecurringImportCard
            integration={this.props.integration}
            targets={this.props.targetOptions}
            lastImport={this.props.lastSync}
          />
        ) : null}
        {this.props.integration.type === 'dynamics' ? (
          <DynamicsRecurringImportCard
            integration={this.props.integration}
            targets={this.props.targetOptions}
            lastImport={this.props.lastSync}
          />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    params: state.location.params,
    fields: state.fields,
    field_mappings: state.field_mappings,
    defaultHeaders: state.appState.defaultHeaders,
    accountId: state.appState.loggedInUser.account_id,
    integration: state.integrations.find(
      (i) => i.type === ownProps.integrationType
    ),
  };
};

export default connect(mapStateToProps)(Integration);
